import { useState, useEffect } from 'react';
import { AutocompleteArrayInput, useInput, useRecordContext } from 'react-admin';
import { FormControlLabel, Checkbox, Box } from '@mui/material';
import { CATALOG_AVAILABILITY_OPTIONS } from '@src/types';

interface AvailabilityInputProps {
  source: string;
  label: string;
}

/**
 * Custom availability input that shows a checkbox when availability is inconsistent
 * across variants, allowing the user to make them consistent.
 */
const AvailabilityInput = ({ source, label }: AvailabilityInputProps) => {
  const record = useRecordContext();
  const isInconsistentAvailability = record?.isInconsistentAvailability || false;

  // When availability is already consistent, default to true so changes are saved.
  // When inconsistent, default to false so the user must opt in via the checkbox.
  const [makeConsistent, setMakeConsistent] = useState(!isInconsistentAvailability);

  // Get the makeAvailabilitiesConsistent field input hook
  const { field: makeConsistentField } = useInput({
    source: 'makeAvailabilitiesConsistent',
  });

  const availabilityChoices = CATALOG_AVAILABILITY_OPTIONS.map(option => ({
    id: option,
    name: option,
  }));

  // Sync the checkbox state with the form field
  useEffect(() => {
    makeConsistentField.onChange(makeConsistent);
  }, [makeConsistent, makeConsistentField]);

  // Sync makeConsistent when record changes (e.g., after save)
  useEffect(() => {
    setMakeConsistent(!isInconsistentAvailability);
  }, [isInconsistentAvailability]);

  return (
    <Box>
      <AutocompleteArrayInput
        source={source}
        label={label}
        choices={availabilityChoices}
        fullWidth
        onChange={() => {
          // When availability changes, set makeConsistent to true if inconsistent
          if (isInconsistentAvailability) {
            setMakeConsistent(true);
          }
        }}
      />
      {isInconsistentAvailability && (
        <FormControlLabel
          control={
            <Checkbox
              checked={makeConsistent}
              onChange={e => setMakeConsistent(e.target.checked)}
            />
          }
          label="The availability reflected above differs for certain child variants of this parent product. Click checkbox to make data consistent across all child variants."
          sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
      )}
    </Box>
  );
};

export default AvailabilityInput;
